import { useQuery } from "@tanstack/react-query";
import { leagueApi } from "@bw/shared/api";
import type { LeagueStandingResponse } from "@bw/shared/types/generated";

/**
 * A division's standings table (GET /leagues/divisions/{division_id}/standings →
 * LeagueStandingResponse[]). The rows are SERVER-ORDERED (points desc, wins desc,
 * player_id) and SERVER-ENRICHED — each carries a `display_name` (null when the
 * backend could not resolve the player; the consuming page renders "—", NEVER the
 * raw player_id). The page renders the list verbatim: it never re-sorts, never
 * re-derives a rank, and never resolves a name client-side.
 *
 * This is a PUBLIC division read (the standings endpoint is anonymous-readable),
 * but it is reached from the authed "My leagues" surface; `enabled` is gated only
 * on a present divisionId so a bare/empty route param never fires a request. Calls
 * the SHARED `leagueApi.getStandings(divisionId)` (the canonical division-standings
 * client method — there is no second method for the same endpoint).
 */
export function useDivisionStandings(divisionId: string) {
  return useQuery<LeagueStandingResponse[]>({
    queryKey: ["leagues", "division-standings", divisionId],
    queryFn: () => leagueApi.getStandings(divisionId),
    enabled: Boolean(divisionId),
  });
}
